import { useAuth } from "app/hooks";
import useWidth from "app/hooks/useWidth";
import { MEMOINDEX } from "app/utils/memoIndex";
import { FC, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  useCheckWhitelistStatusQuery,
  useFindByIdQuery,
  useFindByIdWithStatusQuery,
  useGetApplicationDetailsQuery,
  useGetPurchaseDetailsQuery,
  useGetTotalBalanceQuery,
} from "store/api/new.api";
import { useAccount } from "wagmi";
import CampaignTracker from "../CampaignTracker/CampaignTracker";
import FullScreenLoader from "../loader/FullScreenLoader";
import PageWrapper from "../page-wrapper/page-wrapper";
import SubscribeForm from "../subscribe-form/subscribe-form";
import CeoMessage from "./CeoMessage/CeoMessage";
import IdoCompleteModal from "./IdoCompleteModal/IdoCompliteModal";
import IdoInfoComponent from "./IdoInfoComponent/IdoInfoComponent";
import PreOrderForm, { PreOrderFormRef } from "./PreOrderForm/PreOrderForm";
import ProjectOverviewWidget from "./ProjectOverviewWidget/project-overview-widget";
import styles from "./ido-details.module.scss";
import SaleWidget from "./sale-widget/sale-widget";
import StageWidget from "./stages-widget/stages-widget";

const TIMEOUT = 1000 * 8;

const IdoDetail: FC = () => {
  const { idoId } = useParams<{ idoId: string }>();
  const {
    data: idoData,
    isLoading,
    refetch,
    error: idoDataError,
  } = useFindByIdQuery(idoId);
  const { address } = useAccount();
  const isAuth = useAuth();
  const { isMobile } = useWidth();
  const preOrderRef = useRef<PreOrderFormRef | null>(null);

  const legalMessage =
    idoData?.frontEndData?.[18]?.legalMessage?.[0]?.value || "";
  const memoLink =
    idoData?.frontEndData?.[4]?.socialLinks?.find(
      (link: { label: string; value: string }) =>
        link.label.toLowerCase() === "memolink"
    )?.value || "";

  const { data: totalBalance, refetch: totalBalanceRefetch } =
    useGetTotalBalanceQuery({ idoId });

  const chainIds =
    idoData?.supportedNetworks?.map((network: any) => network.chainId) || [];

  const {
    data: wlData,
    refetch: wlDataRefetch,
    error: wlDataError,
  } = useFindByIdWithStatusQuery(
    { idoId, address },
    { skip: !idoId || !address || !isAuth }
  );

  const {
    data: appDetailsData,
    isLoading: appDetailsIsLoading,
    refetch: refetchAppDetails,
    error: appDetailsIsError,
  } = useGetApplicationDetailsQuery(
    {
      chainIds,
      idoId,
      address,
    },
    { skip: !isAuth || !address }
  );

  const {
    data: purchaseDetailsData,
    isLoading: purchaseDetailsIsLoading,
    refetch: refetchPurchaseDetails,
    error: purchaseDetailsIsError,
  } = useGetPurchaseDetailsQuery(
    {
      chainIds,
      idoId,
      address,
    },
    { skip: !isAuth || !address }
  );

  const {
    data: checkWlStatusData,
    isLoading: checkWlStatusLoading,
    error: checkWlStatusError,
    refetch: refetchCheckWlStatus,
  } = useCheckWhitelistStatusQuery(
    {
      idoId,
      address,
    },
    { skip: !isAuth || !address }
  );

  const refetchCount = useRef(0);

  useEffect(() => {
    if (!isAuth) return;

    const errorHandlers = [
      { isError: appDetailsIsError, refetch: refetchAppDetails },
      { isError: purchaseDetailsIsError, refetch: refetchPurchaseDetails },
      { isError: checkWlStatusError, refetch: refetchCheckWlStatus },
      { isError: idoDataError, refetch },
      { isError: wlDataError, refetch: wlDataRefetch },
    ];

    errorHandlers.forEach(({ isError, refetch }) => {
      if (isError && refetchCount.current < 5) {
        refetch();
        refetchCount.current++;
      }
    });
  }, [
    isAuth,
    appDetailsIsError,
    purchaseDetailsIsError,
    checkWlStatusError,
    idoDataError,
    wlDataError,
  ]);

  const isDataLoading =
    isLoading ||
    appDetailsIsLoading ||
    purchaseDetailsIsLoading ||
    checkWlStatusLoading;

  const refetchAllData = () => {
    setTimeout(() => {
      refetch();
      refetchAppDetails();
      refetchPurchaseDetails();
      totalBalanceRefetch();
    }, TIMEOUT);
  };

  const memoData = [
    { memoHeader: idoData?.frontEndData?.[MEMOINDEX.header]?.memoHeader || [] },
    {
      description:
        idoData?.frontEndData?.[MEMOINDEX.description]?.description || [],
    },
    { team: idoData?.frontEndData?.[MEMOINDEX.team]?.team || [] },
    {
      teamPhoto: idoData?.frontEndData?.[MEMOINDEX.teamPhoto]?.teamPhoto || [],
    },
    {
      problemSolution:
        idoData?.frontEndData?.[MEMOINDEX.problemSolution]?.problemSolution ||
        [],
    },
    {
      tokenInfo: idoData?.frontEndData?.[MEMOINDEX.tokenInfo]?.tokenInfo || [],
    },
    { traction: idoData?.frontEndData?.[MEMOINDEX.traction]?.traction || [] },
    {
      tokenUtility:
        idoData?.frontEndData?.[MEMOINDEX.tokenUtility]?.tokenUtility || [],
    },
    { backers: idoData?.frontEndData?.[MEMOINDEX.backers]?.backers || [] },
    {
      competitiveEdge:
        idoData?.frontEndData?.[MEMOINDEX.competitiveEdge]?.competitiveEdge ||
        [],
    },
    { analysis: idoData?.frontEndData?.[MEMOINDEX.analysis]?.analysis || [] },
    {
      opportunitiesRisks:
        idoData?.frontEndData?.[MEMOINDEX.opportunitiesRisks]
          ?.opportunitiesRisks || [],
    },
  ];

  if (isDataLoading) {
    return <FullScreenLoader isLoading={isDataLoading} />;
  }

  if (!idoData) {
    return (
      <PageWrapper>
        <div style={{ color: "red", textAlign: "center", marginTop: "50px" }}>
          No data found for the specified IDO.
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <div className={styles.idoDetail}>
        <StageWidget
          stages={idoData?.frontEndData?.[8]?.stages || []}
          extraStages={idoData?.frontEndData?.[19]?.extraStages || []}
        />
        <div className={styles.wrapper}>
          <ProjectOverviewWidget
            link={memoLink}
            isLoading={isLoading}
            stage={idoData?.currentStage}
          />
        </div>
        {idoData?.currentStage !== "Whitelist" && (
          <SaleWidget
            message={legalMessage}
            idoData={idoData}
            isLoading={isDataLoading}
            totalBalance={totalBalance || 0}
            totalTokens={idoData?.totalTokens}
            sellStartDate={
              (idoData?.stage === "fcfs" && new Date()) ||
              new Date(wlData?.application?.stDate)
            }
            sellEndDate={new Date(idoData?.idoDate?.end)}
            tokenTicker={idoData?.tokenTicker}
            allocationDetails={{
              maxAllocation: Number(appDetailsData?.maxAllocation || 0),
              usedAllocation: Number(appDetailsData?.usedAllocation || 0),
              availableAllocation: Number(
                appDetailsData?.availableAllocation || 0
              ),
              tokenPrice: parseFloat(idoData?.price || 0),
              minAllocation: Number(appDetailsData?.minAllocation || 0),
            }}
            purchaseDetails={{
              totalTokensPurchased: Number(
                purchaseDetailsData?.totalTokensPurchased || 0
              ),
              claimableTokens: Number(
                purchaseDetailsData?.claimableTokens || 0
              ),
              vestingDetails:
                purchaseDetailsData?.vestingDetails || "no data provided",
            }}
            saleDetails={{ isSaleOpen: true }}
            onPurchaseSuccess={refetchAllData}
          />
        )}
        {idoData?.currentStage === "Whitelist" && (
          <PreOrderForm
            message={legalMessage}
            whitelistStartDate={new Date(idoData?.preWhitelistDate?.start)}
            whitelistEndDate={new Date(idoData?.preWhitelistDate?.end)}
            idoData={idoData}
            ticker={idoData?.tokenTicker}
            variant="default"
            onPurchaseSuccess={refetchAllData}
            allocationDetails={{
              maxAllocation: Number(appDetailsData?.maxAllocation || 0),
              usedAllocation: Number(appDetailsData?.usedAllocation || 0),
              availableAllocation: Number(
                appDetailsData?.availableAllocation || 0
              ),
              tokenPrice: parseFloat(idoData?.preWhitelistPrice || 0),
              minAllocation: Number(appDetailsData?.minAllocation || 0),
            }}
            purchaseDetails={{
              totalTokensPurchased: Number(
                purchaseDetailsData?.totalTokensPurchased || 0
              ),
              claimableTokens: Number(
                purchaseDetailsData?.claimableTokens || 0
              ),
              vestingDetails:
                purchaseDetailsData?.vestingDetails || "no data provided",
            }}
          />
        )}
        <IdoInfoComponent
          memoData={memoData}
          ticker={idoData?.tokenTicker}
          currentStage={idoData?.currentStage}
          howToGetPreparedTitle="How to Get Prepared"
          howToGetPreparedDescription={
            idoData?.frontEndData?.[13]?.eywaHowToGet?.[0]?.value || ""
          }
          howToGetPreparedIsLoading={isLoading}
          tokenUtilityData={idoData?.frontEndData?.[3]?.tokenUtilityData || []}
          tokenMetricsData={idoData?.frontEndData?.[1]?.tokenMetricsData || []}
          roundDetailsData={idoData?.frontEndData?.[2]?.roundDetailsData || []}
          specialConditionsTitle="Special Conditions"
          specialConditionsDescription={
            idoData?.frontEndData?.[9]?.smartWlInfo?.[0]?.value || ""
          }
          handleCancelPurchase={() => preOrderRef.current?.cancelPurchase()}
          isCancelSuccessful={false}
          purchaseDetails={{
            totalTokensPurchased: Number(
              purchaseDetailsData?.totalTokensPurchased || 0
            ),
            claimableTokens: Number(purchaseDetailsData?.claimableTokens || 0),
            vestingDetails:
              purchaseDetailsData?.vestingDetails || "no data provided",
          }}
          projectDetailsIsLoading={isLoading}
          projectDetailsYoutubeLink={
            idoData?.frontEndData?.[21]?.youtubeLink?.[0]?.value
          }
          projectDetailsTitle="Project Details"
          projectDetailsDescription={
            idoData?.frontEndData?.[14]?.eywaDescription?.[0]?.value || ""
          }
          projectDetailsSubtitle={
            idoData?.frontEndData?.[15]?.eywaSubtitle?.[0]?.value || ""
          }
        />

        <CeoMessage
          ceoData={idoData?.frontEndData?.[16]?.seoMessage || []}
          ceoPhoto={idoData?.frontEndData?.[20]?.ceoPhoto || []}
        />

        <SubscribeForm
          brevoId={idoData?.frontEndData?.[12]?.brevoId?.[0]?.value}
        />
        <CampaignTracker />
        {idoData?.frontEndData?.[22]?.popupText &&
          new Date(idoData?.idoDate?.end) < new Date() && (
            <IdoCompleteModal
              popupText={idoData?.frontEndData?.[22]?.popupText}
              isIdoFinished={new Date(idoData?.idoDate?.end) < new Date()}
            />
          )}
      </div>
    </PageWrapper>
  );
};

export default IdoDetail;
